import { InputNumber, Modal } from "antd";
import styled from "styled-components";
import { colors } from "src/theme/colors";
import { line, noSteppers, radii, space, text, withAlpha } from "src/theme/ui";

export const Wrapper = styled(Modal)`
  & .ant-modal-content {
    border-radius: ${radii.lg}px;
  }
`;

/**
 * A row of the weights table.
 *
 * The columns are named once, at the top, in the product's quiet label
 * treatment — they had been bold text in two different blues at once, an
 * inline `primary[900]` on every cell fighting a `#0d4ba8` from the parent
 * rule, and heavier than the figures they head.
 */
export const Row = styled.div`
  display: grid;
  /**
   * The name takes the room; the three figures take what a figure needs.
   *
   * At repeat(3, minmax(0, 1fr)) the weight, min and max fields each grew
   * to a quarter of a 1000px dialog, so "10%" sat in a 250px box with two
   * hundred pixels of nothing after it — three times per row. A per-cent is
   * four characters; 104px is the field, and the name gets the rest.
   */
  grid-template-columns: minmax(0, 1fr) repeat(3, 104px);
  align-items: center;
  gap: ${space.md}px;
  padding: 5px 0;

  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr) repeat(3, minmax(0, 1fr));
    gap: ${space.sm}px;
  }

  &.head > * {
    ${text.label}
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 9.5px;
    color: ${colors.gray[500]};
  }

  /* The three heads sit over their own columns, and their columns are
     right-aligned figures. */
  &.head > *:not(:first-child) {
    text-align: right;
  }

  &.head {
    padding-bottom: 8px;
    border-bottom: 1px solid ${line.hairline};
  }
`;

/**
 * The total, at the FOOT of what it totals.
 *
 * It sat above the asset rows, and the "must add up to 100%" warning was
 * placed inside that same grid row spanning the third and fourth columns —
 * so the error text ran under the Min Weight and Max Weight headings and
 * read as though it belonged to them. A sum goes under its addends, and a
 * message about the sum goes under the sum.
 */
export const TotalRow = styled(Row)`
  margin-top: 4px;
  padding-top: 10px;
  border-top: 1px solid ${line.hairline};
`;

/* The word, not a list entry. It had the asset classes' own coloured dot —
   red when the weights were off — so the sum read as a fourth class with a
   status, and the dialog said one thing in three colours. */
export const TotalLabel = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${colors.gray[900]};
`;

export const Note = styled.p<{ $error?: boolean }>`
  margin: 8px 0 0;
  font-size: 11.5px;
  line-height: 1.5;
  color: ${({ $error }) => ($error ? colors.error[600] : colors.gray[500])};
`;

/**
 * What is wrong, how far, and what to do about it.
 *
 * It was one red sentence restating the rule — "The weights must add up to
 * 100%." — under a red total and a red dot. A reader who can see 80% in the
 * total already knows the rule is broken; what they do not have is the
 * ARITHMETIC, and it is the dialog that knows it: twenty points, to add
 * across three classes.
 *
 * A tinted strip rather than loose red type, so the message reads as one
 * object at the foot of the table rather than as more of the table.
 */
export const Alert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: ${space.md}px;
  padding: 10px 12px;
  border: 1px solid ${withAlpha(colors.error[500], 0.24)};
  border-radius: ${radii.md}px;
  background: ${colors.error[25]};

  svg {
    flex: none;
    margin-top: 1px;
    color: ${colors.error[600]};
  }
`;

export const AlertText = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${colors.error[700]};

  b {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

/* Why the button will not press, beside the button. A disabled control
   with no reason next to it is a dead end: the reader can see it is off and
   has nothing to act on. */
export const Blocker = styled.span`
  margin-right: auto;
  font-size: 11.5px;
  line-height: 1.4;
  color: ${colors.gray[500]};
`;

export const Input = styled(InputNumber)`
  ${noSteppers}

  &.ant-input-number {
    width: 100%;
    border-radius: ${radii.control}px;
    border: 1px solid ${line.hairline};
    background: ${colors.white};
    box-shadow: none;
  }

  &.ant-input-number:hover {
    border-color: ${colors.gray[300]};
  }

  &.ant-input-number-focused {
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${withAlpha(colors.primary[500], 0.14)};
  }

  .ant-input-number-input {
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
    /* Right, so three columns of per-cents read down as columns of figures
       rather than as three ragged left edges. */
    text-align: right;
    color: ${colors.gray[900]};
  }
`;

/* A pane, not a tinted slab. Two grey boxes inside a white dialog were the
   same surface drawn three times; a hairline separates them from the dialog
   without repainting it. */
export const Background = styled.div`
  height: 100%;
  padding: ${space.lg}px;
  border: 1px solid ${line.hairline};
  border-radius: ${radii.lg}px;
  background: ${colors.white};
`;

export const Title = styled.h2`
  ${text.hero}
  margin: 0 0 ${space.lg}px;
  font-size: 18px;
  color: ${colors.gray[900]};
`;

export const FieldLabel = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${colors.gray[900]};

  i {
    font-style: normal;
    font-weight: 400;
    color: ${colors.gray[400]};
  }

  b {
    color: ${colors.error[500]};
  }
`;

/* The actions, at the end of the row the reader finishes on. */
export const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${space.sm}px;
  width: 100%;
  margin-top: ${space.lg}px;
  padding-top: ${space.md}px;
  border-top: 1px solid ${line.hairline};
`;

/**
 * The sum, as a reading rather than a field.
 *
 * It was a disabled InputNumber — a control drawn as somewhere to type that
 * then refuses every keystroke, and the only red-bordered "field" on the
 * dialog when the weights did not add up, which reads as a field the reader
 * has filled in wrongly. They have not: the total is the sum of the three
 * fields above it, and the thing to correct is one of those.
 *
 * So it is set as a figure, toned by whether it adds up, and the sentence
 * under it says what to do.
 */
export const TotalValue = styled.span<{ $ok: boolean }>`
  display: inline-flex;
  align-items: center;
  ${text.numeric}
  font-size: 15px;
  font-weight: 600;
  color: ${({ $ok }) => ($ok ? colors.gray[900] : colors.error[600])};
`;
